import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import CTA from "../components/cta";
import "../components/faq.css";
import "./villas-for-sale-abu-dhabi.css";

const keyBenefits = [
  { icon: "fas fa-users", title: "Ideal for small families and working professionals" },
  { icon: "fas fa-ruler-combined", title: "Spacious layouts with separate living and sleeping areas" },
  { icon: "fas fa-user-shield", title: "Better privacy compared to smaller units" },
  { icon: "fas fa-map-marker-alt", title: "Strong availability across prime locations" },
  { icon: "fas fa-dumbbell", title: "Access to modern amenities like gyms, pools, and parking" },
];

const rentalOptions = [
  { icon: "fas fa-door-open", title: "Ready-to-Move Apartments", text: "Fully available units for immediate occupancy with all essential facilities." },
  { icon: "fas fa-couch", title: "Furnished 2BHK Apartments", text: "Ideal for tenants looking for convenience and a hassle-free move-in experience." },
  { icon: "fas fa-gem", title: "Luxury 2BHK Units", text: "High-end apartments with premium interiors, smart features, and scenic views." },
  { icon: "fas fa-coins", title: "Budget-Friendly Options", text: "Affordable choices in well-connected communities without compromising on quality." },
];

const primeLocations = [
  { icon: "fas fa-water", title: "Al Reem Island", text: "Modern apartments with waterfront views" },
  { icon: "fas fa-ticket-alt", title: "Yas Island", text: "Vibrant lifestyle and entertainment access" },
  { icon: "fas fa-home", title: "Khalifa City", text: "Affordable and family-friendly communities" },
  { icon: "fas fa-building", title: "City Center", text: "Close to offices, schools, and essential services" },
];

const faqs = [
  {
    icon: "fas fa-home",
    question: "Is a 2BHK suitable for families in Abu Dhabi?",
    answer: "Yes. A 2BHK apartment is a great solution for small families, as it offers them sufficient space and comfort.",
  },
  {
    icon: "fas fa-couch",
    question: "Can I find furnished 2BHK apartments in Abu Dhabi?",
    answer: "You can easily find a 2BHK apartment for rent in Abu Dhabi, as you have already come to the right place.",
  },
  {
    icon: "fas fa-handshake",
    question: "Why should I choose NV Real Estate?",
    answer: "Nova Vision Real Estate offers expert guidance, verified properties, transparent dealings, and full support to help you find the perfect rental home.",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map(({ question, answer }) => ({
    "@type": "Question",
    name: question,
    acceptedAnswer: { "@type": "Answer", text: answer },
  })),
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://nvrealestate.ae/" },
    { "@type": "ListItem", position: 2, name: "2BHK for Rent in Abu Dhabi", item: "https://nvrealestate.ae/2bhk-for-rent-abu-dhabi" },
  ],
};

function TwoBhkForRentAbuDhabi() {
  const [activeFaq, setActiveFaq] = useState(0);

  return (
    <>
      <Helmet>
        <title>2BHK for Rent in Abu Dhabi – Nvrealestate</title>
        <meta
          name="description"
          content="Searching for a well-located 2bhk for rent in Abu Dhabi? We have access to the best rental properties in Abu Dhabi. So, we can help you rent the best property matching your needs."
        />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <main
        className="villa-page"
        style={{ "--villa-section-image": `url(${process.env.PUBLIC_URL}/assets/images/abu.jpg)` }}
      >
        <section className="villa-hero" aria-labelledby="villa-page-title">
          <div
            className="villa-hero__image"
            aria-hidden="true"
            style={{ backgroundImage: `url("/images/seo/2bhk-for-rent-abu-dhabi/01.png")` }}
          />
          <div className="villa-hero__overlay" aria-hidden="true" />
          <div className="container villa-hero__inner">
            <div className="villa-hero__content">
              <span className="villa-eyebrow">Abu Dhabi rental specialists</span>
              <h1 id="villa-page-title">Find the Best 2BHK for Rent in Abu Dhabi Today!</h1>
              <div className="villa-hero__actions">
                <Link className="villa-button villa-button--primary" to="/property-search-result?offering_type=Rent&city=&developer=&developer_name=&project=&project_name=&property_type=Apartment&bedrooms=2&bathrooms=&minArea=&maxArea=&minPrice=&maxPrice=">
                  Explore apartments
                </Link>
                <a className="villa-button villa-button--ghost" href="tel:+971501577822">
                  Call +971 50 157 7822
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="villa-intro villa-section">
          <div className="container villa-intro__grid">
            <div className="villa-intro__image">
              <img
                src="/images/seo/2bhk-for-rent-abu-dhabi/02.png"
                alt="2BHK apartments for rent in Abu Dhabi"
              />
              <div className="villa-intro__note">
                <strong>Trusted rentals</strong>
                <span>Curated 2BHK options across Abu Dhabi</span>
              </div>
            </div>
            <div className="villa-section-heading villa-intro__content">
              <span className="villa-kicker">Nova Vision Real Estate</span>
              <h2>Find the Best 2BHK for Rent in Abu Dhabi Today!</h2>
              <p>
                Nova Vision Real Estate is a highly acclaimed and reliable real estate company in
                Abu Dhabi. We help people looking for 2BHKs to find the right rental apartments for
                their needs. Rental property seekers count on us because they know we listen to
                them carefully, suggest the best properties to them, help them explore the
                suggested options, and decide on the right property. Connect with us, and we assure
                you that you will have such an excellent experience with us that you will not look
                beyond us for your next requirements.
              </p>
            </div>
          </div>
        </section>

        <section className="villa-benefits villa-benefits--centered-cards villa-section" aria-labelledby="why-2bhk-heading">
          <div className="container">
            <div className="villa-section-heading villa-section-heading--center">
              <span className="villa-kicker">Why a 2BHK</span>
              <h2 id="why-2bhk-heading">Why Choose a 2BHK in Abu Dhabi?</h2>
              <p>
                Living in a 2BHK with your small family provides you with enough space for
                comfortable living, privacy protection, and that on a reasonable budget.
              </p>
              <p><strong>Key Benefits:</strong></p>
            </div>
            <div className="villa-benefits__grid">
              {keyBenefits.map((benefit, index) => (
                <article className="villa-benefit-card" key={benefit.title}>
                  <span className="villa-benefit-card__number" aria-hidden="true">0{index + 1}</span>
                  <span className="villa-benefit-card__icon" aria-hidden="true"><i className={benefit.icon} /></span>
                  <h3>{benefit.title}</h3>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="villa-benefits villa-section" aria-labelledby="rental-options-heading">
          <div className="container">
            <div className="villa-section-heading villa-section-heading--center">
              <span className="villa-kicker">Rental options</span>
              <h2 id="rental-options-heading">2BHK Rental Options in Abu Dhabi</h2>
              <p>
                NV Real Estate offers a carefully curated selection of 2BHK apartments across Abu
                Dhabi's most sought-after areas.
              </p>
            </div>
            <div className="villa-benefits__grid">
              {rentalOptions.map((option, index) => (
                <article className="villa-benefit-card" key={option.title}>
                  <span className="villa-benefit-card__number" aria-hidden="true">0{index + 1}</span>
                  <span className="villa-benefit-card__icon" aria-hidden="true"><i className={option.icon} /></span>
                  <h3>{option.title}</h3>
                  <p>{option.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="villa-benefits villa-section" aria-labelledby="locations-heading">
          <div className="container">
            <div className="villa-section-heading villa-section-heading--center">
              <span className="villa-kicker">Prime locations</span>
              <h2 id="locations-heading">Prime Locations for Renting a 2BHK</h2>
              <p>
                If you are new to Abu Dhabi and are unaware of the areas popular among renters, then
                we will name a few of them for you.
              </p>
            </div>
            <div className="villa-benefits__grid">
              {primeLocations.map((location, index) => (
                <article className="villa-benefit-card" key={location.title}>
                  <span className="villa-benefit-card__number" aria-hidden="true">0{index + 1}</span>
                  <span className="villa-benefit-card__icon" aria-hidden="true"><i className={location.icon} /></span>
                  <h3>{location.title}</h3>
                  <p>{location.text}</p>
                </article>
              ))}
            </div>
            <p className="villa-process__closing">
              With NV Real Estate, you can explore the best locations using our advanced property
              finder Abu Dhabi system.
            </p>
          </div>
        </section>

        <section className="villa-advisor villa-section">
          <div className="container">
            <div className="villa-advisor__card">
              <div>
                <span className="villa-kicker">Why Nova Vision</span>
                <h2>Why Trust Nova Vision Real Estate for a 2BHK in Abu Dhabi?</h2>
                <p>
                  NV Real Estate is your most reliable companion in your search for a 2BHK rental.
                  We have already helped many people to rent residential properties for their
                  needs, and most of them appreciate us.
                </p>
                <p>Here are some of the top reasons that make us a dependable choice for your needs.</p>
                <a className="villa-button villa-button--light" href="https://wa.me/971501577822" target="_blank" rel="noreferrer">
                  Chat on WhatsApp
                </a>
                <p className="villa-advisor__closing">
                  Don't waste your valuable time and find your rental property fast with us.
                </p>
              </div>
              <ul>
                <li><strong>Market Expertise</strong> &mdash; We understand Abu Dhabi's rental trends and help you secure the best deals.</li>
                <li><strong>Verified Listings</strong> &mdash; All properties are carefully checked to ensure authenticity and value.</li>
                <li><strong>Transparent Process</strong> &mdash; No hidden information&mdash;only clear and honest communication.</li>
                <li><strong>End-to-End Support</strong> &mdash; From property selection to final agreement, we assist you at every step.</li>
                <li><strong>Client-Focused Approach</strong> &mdash; We prioritize your comfort, budget, and lifestyle needs.</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="nv-faq-section villa-faq" aria-labelledby="villa-faq-title">
          <div className="nv-faq-container">
            <header className="nv-faq-heading">
              <span className="nv-faq-label">Renter questions</span>
              <h2 id="villa-faq-title">Frequently Asked Questions</h2>
              <p>Practical answers to help you begin your 2BHK search with confidence.</p>
            </header>

            <div className="villa-faq__main">
              <div className="nv-faq-accordion">
                {faqs.map((faq, index) => {
                  const isActive = activeFaq === index;
                  return (
                    <article className={`nv-faq-item${isActive ? " active" : ""}`} key={faq.question}>
                      <button
                        className="nv-faq-question"
                        type="button"
                        onClick={() => setActiveFaq((current) => (current === index ? null : index))}
                        aria-expanded={isActive}
                        aria-controls={`bhk-faq-answer-${index}`}
                      >
                        <span className="nv-faq-question-icon"><i className={faq.icon} /></span>
                        <strong>{faq.question}</strong>
                        <span className="nv-faq-toggle" aria-hidden="true" />
                      </button>
                      <div
                        className="nv-faq-answer"
                        id={`bhk-faq-answer-${index}`}
                        aria-hidden={!isActive}
                      >
                        <p>{faq.answer}</p>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>

            <div className="nv-faq-trust-strip">
              <div className="nv-faq-trust-item"><i className="fas fa-home" /><div><strong>Curated Homes</strong><span>Matched to Your Brief</span></div></div>
              <div className="nv-faq-trust-item"><i className="fas fa-map-marker-alt" /><div><strong>Local Knowledge</strong><span>Across Abu Dhabi</span></div></div>
              <div className="nv-faq-trust-item"><i className="fas fa-file-signature" /><div><strong>Transparent Process</strong><span>No Hidden Information</span></div></div>
              <div className="nv-faq-trust-item"><i className="fas fa-headset" /><div><strong>Dedicated Advisor</strong><span>One Point of Contact</span></div></div>
            </div>
            <p className="villa-faq__contact">
              Call us now at <a href="tel:+971501577822">+971-501577822</a>. You can also email your
              requirements to <a href="mailto:info@nvrealestate.ae">info@nvrealestate.ae</a>.
            </p>
          </div>
        </section>
      </main>

      <CTA />

      <style>{`
        .villa-benefits--centered-cards .villa-benefits__grid {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 24px;
        }
        .villa-benefits--centered-cards .villa-benefit-card {
          flex: 1 1 260px;
          max-width: 300px;
        }
        @media (max-width: 700px) {
          .villa-benefits--centered-cards .villa-benefit-card {
            flex: 1 1 100%;
            max-width: 420px;
          }
        }
      `}</style>
    </>
  );
}

export default TwoBhkForRentAbuDhabi;